import { useEffect, useState } from "react";
import { CalendarDays, SlidersHorizontal, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PERIODS, periodLabel, useFilters } from "@/context/FilterContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DatePicker } from "@/components/DatePicker";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const HOURS = [...Array(24).keys()];
let optionsCache = null;

const Section = ({ title, action, children }) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-700">{title}{action}</div>
    {children}
  </div>
);

const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`min-w-0 rounded-md border px-1.5 py-1 text-[11px] transition-colors sm:px-2 ${on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{children}</button>
);

const CheckList = ({ items, selected, onToggle, testPrefix }) => (
  <div className="mt-2 max-h-40 space-y-0.5 overflow-y-auto pr-1">
    {items.map((it, i) => (
      <label key={it.value} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-slate-50" data-testid={`${testPrefix}-${it.tid ?? i}`}>
        <Checkbox checked={selected.includes(it.value)} onCheckedChange={() => onToggle(it.value)} /><span className="truncate">{it.label}</span>
        {it.extra && <span className="ml-auto shrink-0 text-[10px] text-slate-400">{it.extra}</span>}
      </label>
    ))}
  </div>
);

const Clear = ({ n, onClick, testId }) => n > 0 && <button type="button" data-testid={testId} className="font-normal text-blue-600" onClick={onClick}>Deseleziona ({n})</button>;

export const FilterBar = () => {
  const { user } = useAuth();
  const { filters, setFilters, reset, count } = useFilters();
  const [draft, setDraft] = useState(filters);
  const [stores, setStores] = useState([]);
  const [opts, setOpts] = useState(optionsCache || { cities: [], provinces: [], segments: [] });
  const [q, setQ] = useState("");
  const [cq, setCq] = useState("");
  const [pq, setPq] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDraft(filters);
    api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
    if (!optionsCache) api.get("/filters/options").then((r) => { optionsCache = r.data; setOpts(r.data); }).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (k, v) => setDraft({ ...draft, [k]: draft[k].includes(v) ? draft[k].filter((x) => x !== v) : [...draft[k], v] });
  const shownStores = stores.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).map((s) => ({ value: s.id, label: s.name, tid: s.id }));
  const cities = opts.cities.filter((c) => c.name.toLowerCase().includes(cq.toLowerCase())).sort((a, b) => draft.cities.includes(b.name) - draft.cities.includes(a.name)).slice(0, 150)
    .map((c, i) => ({ value: c.name, label: c.name, extra: c.prov, tid: i }));
  const provs = opts.provinces.filter((p) => p.code.toLowerCase().includes(pq.toLowerCase())).map((p) => ({ value: p.code, label: p.code, extra: p.count ? `${p.count} clienti` : "", tid: p.code }));
  const groups = [...new Set(opts.segments.map((s) => s.group))];

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" data-testid="global-filters-button" className={count ? "border-blue-300 bg-blue-50 text-blue-700" : ""}>
            <SlidersHorizontal className="mr-2 h-4 w-4" />Filtri{count ? ` (${count})` : ""}
            <span className="ml-2 hidden items-center gap-1 border-l border-slate-200 pl-2 text-xs font-normal text-slate-500 md:flex" data-testid="global-filters-period"><CalendarDays className="h-3.5 w-3.5" />{periodLabel(filters)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[min(94vw,720px)] bg-white p-0" data-testid="global-filters-panel">
          <div className="grid max-h-[70vh] min-w-0 gap-5 overflow-y-auto overflow-x-hidden p-4 md:grid-cols-2">
            <div className="min-w-0 space-y-4">
              <Section title="Periodo">
                <div className="flex flex-wrap gap-1">
                  {PERIODS.map(([v, l]) => <Chip key={v} on={draft.period === v} testId={`filter-period-${v}`} onClick={() => setDraft({ ...draft, period: v })}>{l}</Chip>)}
                </div>
                {draft.period === "custom" && <div className="mt-2 grid grid-cols-2 gap-2">
                  <div className="text-[11px] text-slate-500">Da<DatePicker testId="filter-date-from" placeholder="gg/mm/aaaa" value={draft.from} max={draft.to || new Date().toISOString().slice(0, 10)} onChange={(v) => setDraft({ ...draft, from: v })} /></div>
                  <div className="text-[11px] text-slate-500">A<DatePicker testId="filter-date-to" placeholder="gg/mm/aaaa" value={draft.to} min={draft.from} max={new Date().toISOString().slice(0, 10)} onChange={(v) => setDraft({ ...draft, to: v })} /></div>
                </div>}
              </Section>
              <Section title="Segmento">
                <Select value={draft.segment || "none"} onValueChange={(v) => setDraft({ ...draft, segment: v === "none" ? "" : v, segmentLabel: opts.segments.find((s) => s.value === v)?.label || "" })}>
                  <SelectTrigger className="h-8" data-testid="filter-segment-select"><SelectValue placeholder="Tutti i clienti" /></SelectTrigger>
                  <SelectContent className="max-h-72 bg-white">
                    <SelectItem value="none">Tutti i clienti</SelectItem>
                    {draft.segment && !opts.segments.some((s) => s.value === draft.segment) && <SelectItem value={draft.segment}>{draft.segmentLabel || draft.segment}</SelectItem>}
                    {groups.map((g) => <SelectGroup key={g}><SelectLabel className="text-[11px] text-slate-400">{g}</SelectLabel>
                      {opts.segments.filter((s) => s.group === g).map((s) => <SelectItem key={s.value} value={s.value} data-testid={`filter-segment-${s.value}`}>{s.label}</SelectItem>)}</SelectGroup>)}
                  </SelectContent>
                </Select>
              </Section>
              <Section title="Giorni di vendita">
                <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                  {DAYS.map((d, i) => <Chip key={d} on={draft.days.includes(i + 1)} testId={`filter-day-${i + 1}`} onClick={() => toggle("days", i + 1)}>{d}</Chip>)}
                </div>
              </Section>
              <Section title="Fascia oraria">
                <div className="grid grid-cols-2 gap-2">
                  {[0, 1].map((j) => (
                    <Select key={j} value={String(draft.hours[j])} onValueChange={(v) => { const h = [...draft.hours]; h[j] = Number(v); if (h[0] > h[1]) h[1 - j] = h[j]; setDraft({ ...draft, hours: h }); }}>
                      <SelectTrigger className="h-8" data-testid={`filter-hour-${j ? "to" : "from"}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64 bg-white">{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{j ? `${String(h).padStart(2, "0")}:59` : `${String(h).padStart(2, "0")}:00`}</SelectItem>)}</SelectContent>
                    </Select>
                  ))}
                </div>
              </Section>
            </div>
            <div className="min-w-0 space-y-4">
              <Section title="Punti vendita" action={<Clear n={draft.stores.length} testId="filter-stores-clear" onClick={() => setDraft({ ...draft, stores: [] })} />}>
                {user?.allowed_stores?.length > 1 && <div className="mb-2 flex flex-wrap items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px]" data-testid="filter-my-stores">
                  <span className="mr-1 font-medium text-emerald-800">I miei {user.allowed_stores.length} negozi:</span>
                  <button type="button" data-testid="filter-my-stores-all" onClick={() => setDraft({ ...draft, stores: [] })} className={`rounded px-2 py-0.5 ${!draft.stores.length ? "bg-emerald-600 text-white" : "bg-white text-emerald-800 hover:bg-emerald-100"}`}>Tutti insieme</button>
                  {stores.map((s) => <button key={s.id} type="button" data-testid={`filter-my-store-${s.id}`} onClick={() => setDraft({ ...draft, stores: [s.id] })} className={`rounded px-2 py-0.5 ${draft.stores.length === 1 && draft.stores[0] === s.id ? "bg-emerald-600 text-white" : "bg-white text-emerald-800 hover:bg-emerald-100"}`}>{s.name}</button>)}
                </div>}
                <Input data-testid="filter-store-search" className="h-8" placeholder="Cerca punto vendita" value={q} onChange={(e) => setQ(e.target.value)} />
                <CheckList items={shownStores} selected={draft.stores} onToggle={(v) => toggle("stores", v)} testPrefix="filter-store" />
              </Section>
              <Section title="Province clienti" action={<Clear n={draft.provs.length} testId="filter-provs-clear" onClick={() => setDraft({ ...draft, provs: [] })} />}>
                <Input data-testid="filter-prov-search" className="h-8" placeholder="Cerca provincia (es. SA)" value={pq} onChange={(e) => setPq(e.target.value)} />
                <CheckList items={provs} selected={draft.provs} onToggle={(v) => toggle("provs", v)} testPrefix="filter-prov" />
              </Section>
              <Section title="Comuni clienti" action={<Clear n={draft.cities.length} testId="filter-cities-clear" onClick={() => setDraft({ ...draft, cities: [] })} />}>
                <Input data-testid="filter-city-search" className="h-8" placeholder="Cerca comune" value={cq} onChange={(e) => setCq(e.target.value)} />
                <CheckList items={cities} selected={draft.cities} onToggle={(v) => toggle("cities", v)} testPrefix="filter-city" />
              </Section>
            </div>
          </div>
          <div className="flex justify-between border-t border-slate-100 p-3">
            <Button size="sm" variant="ghost" data-testid="filters-reset-button" onClick={() => { reset(); setOpen(false); }}>Azzera</Button>
            <Button size="sm" data-testid="filters-apply-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => { setFilters(draft); setOpen(false); }}>Applica filtri</Button>
          </div>
        </PopoverContent>
      </Popover>
      {count > 0 && <button data-testid="filters-clear-chip" onClick={reset} className="hidden items-center gap-1 text-xs text-slate-500 hover:text-rose-600 sm:flex"><X className="h-3 w-3" />azzera</button>}
    </div>
  );
};
